import { addGameObject, getObjectsByType } from './entity';
import { setVisibilityState } from './simulate';
import { Ease, v2 } from './helpers';
import { ObjectType } from './shared/api_shared_types';
import {
  Entity,
  ImageObject,
  JourneyMapAnchor,
  LineStyle,
  VisibilityState,
  GameManager,
  GameObjectController,
  JourneyMap,
  MoveMode,
  RenderItemType,
  Shape,
  TextAlignHor,
  type V2,
} from './types';
import * as DataController from './controllers/data_controller';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageImage,
  type RenderPackageItem,
  type RenderPackageLine,
  type RenderPackagePoint,
  type RenderPackageText,
} from './render_package';
import { COLORS } from './constants-colors';
import { dt, debugState } from '.';

const DEFAULT_IMAGE_ID = 'map2';
const DEFAULT_WIDTH_PX = 2500;

const LABEL_FONT_SIZE = 48;
const LABEL_GAP = 16;

// Hand-drawn circle images drawn around each anchor instead of a plain dot. Each SVG bakes in its
// own size (`scale`), so the image is drawn at one fixed width — the size differences come from the
// art, not from scaling here. Each anchor's radius is set to the drawn circle's radius (see
// getCircleRadius) so the route lines trim to roughly the edge of the ink.
const CIRCLE_VARIATIONS: { id: string; scale: number }[] = [
  { id: 'circle1', scale: 0.6 },
  { id: 'circle2', scale: 0.76 },
  { id: 'circle3', scale: 0.92 },
  { id: 'circle4', scale: 1.08 },
  { id: 'circle5', scale: 1.24 },
  { id: 'circle6', scale: 1.4 },
];
const CIRCLE_IMAGE_ASPECT = 220 / 260; // svg viewBox height / width
const CIRCLE_IMAGE_WIDTH = 200; // fixed image width in the map's local space
// Mean drawn radius as a fraction of image width: avg(rx 76, ry 64) / viewBox width 260.
const CIRCLE_DRAWN_RADIUS_RATIO = 70 / 260;

// Radius of the circle actually drawn in the SVG for the given anchor index, in map-local units.
function getCircleRadius(index: number): number {
  const variation = CIRCLE_VARIATIONS[index % CIRCLE_VARIATIONS.length];
  return CIRCLE_IMAGE_WIDTH * CIRCLE_DRAWN_RADIUS_RATIO * variation.scale;
}

const DEFAULT_ANCHORS: Omit<JourneyMapAnchor, 'radius'>[] = [
  // left side — alternating closer/further
  {
    relativePos: v2(-0.3, 0.08),
    destinationPos: v2(0.62, 0.18),
    occupiedById: null,
    lerpSpeed: 2.5,
    color: COLORS.BLACK_100,
    label: 'Northland/Whangarei',
    routeOrder: 15,
  },
  {
    relativePos: v2(0.4, 0.25),
    destinationPos: v2(0.69, 0.28),
    occupiedById: null,
    lerpSpeed: 7.0,
    color: COLORS.BLACK_100,
    label: 'Auckland',
    routeOrder: 14,
  },
  {
    relativePos: v2(-0.36, 0.48),
    destinationPos: v2(0.57, 0.38),
    occupiedById: null,
    lerpSpeed: 4.0,
    color: COLORS.BLACK_100,
    label: 'Hamilton/Waikato',
    routeOrder: 12,
  },
  {
    relativePos: v2(0.2, 0.5),
    destinationPos: v2(0.48, 0.5),
    occupiedById: null,
    lerpSpeed: 9.5,
    color: COLORS.BLACK_100,
    label: 'Taranaki/New Plymouth',
    routeOrder: 8,
  },
  {
    relativePos: v2(-0.32, 0.85),
    destinationPos: v2(0.55, 0.58),
    occupiedById: null,
    lerpSpeed: 3.0,
    color: COLORS.BLACK_100,
    label: 'Whanganui/Manawatu',
    routeOrder: 7,
  },
  {
    relativePos: v2(0.6, 0.88),
    destinationPos: v2(0.37, 0.77),
    occupiedById: null,
    lerpSpeed: 6.5,
    color: COLORS.BLACK_100,
    label: 'West Coast/Greymouth',
    routeOrder: 4,
  },
  // right side — alternating closer/further
  {
    relativePos: v2(1.22, 0.33),
    destinationPos: v2(0.69, 0.4),
    occupiedById: null,
    lerpSpeed: 8.0,
    color: COLORS.BLACK_100,
    label: 'Tauranga/Bay of Plenty',
    routeOrder: 11,
  },
  {
    relativePos: v2(1.1, 0.5),
    destinationPos: v2(0.69, 0.52),
    occupiedById: null,
    lerpSpeed: 3.5,
    color: COLORS.BLACK_100,
    label: "Hawke's Bay/Napier",
    routeOrder: 10,
  },
  {
    relativePos: v2(0.9, 0.59),
    destinationPos: v2(0.55, 0.55),
    occupiedById: null,
    lerpSpeed: 5.5,
    color: COLORS.BLACK_100,
    label: 'Wellington',
    routeOrder: 9,
  },
  {
    relativePos: v2(1.28, 0.1),
    destinationPos: v2(0.9, 0.3),
    occupiedById: null,
    lerpSpeed: 10.0,
    color: COLORS.BLACK_100,
    label: 'Marlborough/Blenheim',
    routeOrder: 13,
  },
  {
    relativePos: v2(1.32, 0.9),
    destinationPos: v2(0.3, 0.73),
    occupiedById: null,
    lerpSpeed: 2.0,
    color: COLORS.BLACK_100,
    label: 'Dunedin/Otago',
    routeOrder: 4,
  },
  // top-left overlap
  {
    relativePos: v2(1.08, 1.2),
    destinationPos: v2(0.46, 0.65),
    occupiedById: null,
    lerpSpeed: 6.0,
    color: COLORS.BLACK_100,
    label: 'Nelson/Tasman',
    routeOrder: 6,
  },
  {
    relativePos: v2(-0.16, 0.7),
    destinationPos: v2(0.33, 0.69),
    occupiedById: null,
    lerpSpeed: 4.5,
    color: COLORS.BLACK_100,
    label: 'Christchurch/Canterbury',
    routeOrder: 5,
  },
  // bottom-right overlap
  {
    relativePos: v2(-0.2, 0.9),
    destinationPos: v2(0.2, 0.76),
    occupiedById: null,
    lerpSpeed: 8.5,
    color: COLORS.BLACK_100,
    label: 'Queenstown/Wanaka',
    routeOrder: 3,
  },
  {
    relativePos: v2(0.3, 1.06),
    destinationPos: v2(0.3, 0.82),
    occupiedById: null,
    lerpSpeed: 5.0,
    color: COLORS.BLACK_100,
    label: 'Invercargill/Southland',
    routeOrder: 2,
  },
];

export function controller(): GameObjectController {
  return {
    getNew: null,
    sync: null,
    update,
    createPackage,
  };
}

export function createJourneyMap(
  manager: GameManager,
  ent: Entity,
  props: Partial<JourneyMap> = {},
): JourneyMap {
  const journeyMap: JourneyMap = {
    ...ent,
    shape: Shape.RECT,

    objectType: ObjectType.JOURNEY_MAP,
    imageId: DEFAULT_IMAGE_ID,
    aspect: 0,
    imageState: '',

    appearStyle: 'fade_and_scale',
    onImageError: 'white',

    widthPx: DEFAULT_WIDTH_PX,

    requestUpdateDimensions: false,

    rotationSpeed: 0,

    alpha: 1,
    appearAlpha: 1,

    unloadImageOnDispose: false,

    anchors: DEFAULT_ANCHORS.map((a, i) => ({ ...a, radius: getCircleRadius(i) })),

    ...props,
  };

  if (journeyMap.moveMode === MoveMode.NONE) {
    journeyMap.moveMode = MoveMode.STATIC;
  }
  journeyMap.influenceKinetic = false;

  journeyMap.clickable = false;
  journeyMap.allowDrag = false;

  if (journeyMap.appearStyle === 'fade' || journeyMap.appearStyle === 'fade_and_scale') {
    journeyMap.appearAlpha = 0;
  }

  addGameObject(manager, journeyMap);

  setVisibilityState(journeyMap, VisibilityState.APPEARING);

  return journeyMap;
}

function update(journeyMap: JourneyMap) {
  let isDirty = false;

  const state = journeyMap.visibilityState;
  const progress = journeyMap.stateProgress;

  if (state === VisibilityState.APPEARING) {
    const t = Ease.outBack(progress);

    if (journeyMap.appearStyle === 'fade') {
      journeyMap.appearAlpha = progress;
    } else if (journeyMap.appearStyle === 'fade_and_scale') {
      journeyMap.scale = 0.8 + 0.2 * t;
      journeyMap.appearAlpha = progress;
    } else {
      journeyMap.scale = 1;
      journeyMap.appearAlpha = 1;
    }
    isDirty = true;
  } else if (state === VisibilityState.DISAPPEARING || state === VisibilityState.HIDING) {
    if (journeyMap.appearStyle === 'fade' || journeyMap.appearStyle === 'fade_and_scale') {
      journeyMap.appearAlpha = 1 - progress;
    }
    isDirty = true;
  } else if (journeyMap.entityStateChanged && state === VisibilityState.DEFAULT) {
    journeyMap.appearAlpha = 1;
    journeyMap.scale = 1;
    isDirty = true;
  }

  let doUpdateDimensions = journeyMap.requestUpdateDimensions;

  if (journeyMap.imageState !== 'error' && journeyMap.imageState !== 'success') {
    const imageState = DataController.getImageState(journeyMap.imageId);

    if (imageState !== journeyMap.imageState) {
      journeyMap.imageState = imageState;

      if (journeyMap.imageState === 'success') {
        journeyMap.aspect = DataController.getImageAspectRatio(journeyMap.imageId);
        doUpdateDimensions = true;
      }
    }
  }

  if (journeyMap.aspect && doUpdateDimensions) {
    journeyMap.requestUpdateDimensions = false;

    updateDimensions(journeyMap);

    isDirty = true;
    journeyMap.layoutChanged = true;
  }

  if (journeyMap.rotationSpeed !== 0) {
    journeyMap.rotation += journeyMap.rotationSpeed * dt;
    isDirty = true;
  }

  return isDirty;
}

function updateDimensions(journeyMap: JourneyMap) {
  journeyMap.width = journeyMap.widthPx;
  journeyMap.height = journeyMap.widthPx / journeyMap.aspect;
}

export function getAnchorTargetPos(map: JourneyMap, anchorIndex: number): V2 {
  const anchor = map.anchors[anchorIndex];
  const relOffX = (anchor.relativePos.x - 0.5) * map.width;
  const relOffY = (anchor.relativePos.y - 0.5) * map.height;
  const cos = Math.cos(map.rotation);
  const sin = Math.sin(map.rotation);
  return v2(map.pos.x + cos * relOffX - sin * relOffY, map.pos.y + sin * relOffX + cos * relOffY);
}

function createPackage(
  object: JourneyMap,
  position: V2,
  scale: number,
  rotation: number,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  const alpha = object.alpha * object.appearAlpha;

  const aspect = DataController.getImageAspectRatio(object.imageId);

  if (aspect && object.height && object.width) {
    const w = object.width;
    const h = object.height;

    const im: RenderPackageImage = {
      type: RenderItemType.image,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      imageId: object.imageId,
      positionX: position.x,
      positionY: position.y,

      positionLocalX: -w / 2,
      positionLocalY: -h / 2,

      rotation,

      width: w,
      height: h,
      shape: object.shape,

      radius: object.radius,
      opacity: alpha,

      flipHorizontally: false,
    };
    rPackage.push(im);
  }

  const imageObjects = getObjectsByType<ImageObject>(object.manager, ObjectType.IMAGE_OBJECT);
  const photoById = new Map<number, ImageObject>();
  for (const photo of imageObjects) {
    photoById.set(photo.id, photo);
  }

  const canvasLayerScale = scale / object.scale;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  // Destination dot center in the render transform's local space: positionX/Y is the origin,
  // coordinates are multiplied by canvasLayerScale to get screen pixels. In this space the dot's
  // radius is anchor.radius (same value passed to the dot circle below).
  const getAnchorLocalPos = (anchor: JourneyMapAnchor): V2 => {
    const dRelOffX = (anchor.destinationPos.x - 0.5) * object.width;
    const dRelOffY = (anchor.destinationPos.y - 0.5) * object.height;
    return v2(
      (cos * dRelOffX - sin * dRelOffY) * object.scale,
      (sin * dRelOffX + cos * dRelOffY) * object.scale,
    );
  };

  const lineStyle: LineStyle = {
    color: COLORS.RED_SALSA,
    dash: [12, 18],
    width: 9,
    bezier: false,
  };

  // Route: connect destinations in ascending routeOrder. Normally only active destinations
  // (those with an attached photo) are linked; when the debug flag is on every anchor's circle
  // is shown, so connect all of them to preview the full route.
  // Drawn before the dots/labels so the circles render on top of the line ends.
  const routeAnchors = debugState.showMapDestinations
    ? object.anchors.slice().sort((a, b) => a.routeOrder - b.routeOrder)
    : object.anchors
        .filter((a) => a.occupiedById !== null && photoById.has(a.occupiedById))
        .sort((a, b) => a.routeOrder - b.routeOrder);

  const routeStyle: LineStyle = {
    color: COLORS.BLACK_100,
    dash: [],
    width: 7,
    bezier: false,
  };

  for (let i = 0; i < routeAnchors.length - 1; i++) {
    const from = routeAnchors[i];
    const to = routeAnchors[i + 1];
    const a = getAnchorLocalPos(from);
    const b = getAnchorLocalPos(to);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.hypot(dx, dy);
    if (dist === 0) {
      continue;
    }
    const ux = dx / dist;
    const uy = dy / dist;
    // Trim each endpoint inward by the circle radius so the line meets the edge, not the center.
    const posA = v2(a.x + ux * from.radius, a.y + uy * from.radius);
    const posB = v2(b.x - ux * to.radius, b.y - uy * to.radius);

    const routeLine: RenderPackageLine = {
      type: RenderItemType.line,
      ...EMPTY_RENDER_TRANSFORM,
      positionX: position.x,
      positionY: position.y,
      scale: canvasLayerScale,
      posA,
      posB,
      style: routeStyle,
    };
    rPackage.push(routeLine);
  }

  for (let i = 0; i < object.anchors.length; i++) {
    const anchor = object.anchors[i];

    const photo = anchor.occupiedById === null ? null : photoById.get(anchor.occupiedById) || null;
    const hasPhoto = photo != null;

    // Without a photo, only draw the destination when the debug flag is on.
    if (!hasPhoto && !debugState.showMapDestinations) {
      continue;
    }

    const posB = getAnchorLocalPos(anchor);

    if (hasPhoto) {
      const posA = v2(photo.pos.x - object.pos.x, photo.pos.y - object.pos.y);

      const line: RenderPackageLine = {
        type: RenderItemType.line,
        ...EMPTY_RENDER_TRANSFORM,
        positionX: position.x,
        positionY: position.y,
        scale: canvasLayerScale,
        posA,
        posB,
        style: lineStyle,
      };
      rPackage.push(line);
    }

    const circleWidth = CIRCLE_IMAGE_WIDTH;
    const circleHeight = circleWidth * CIRCLE_IMAGE_ASPECT;
    const circleImageId = CIRCLE_VARIATIONS[i % CIRCLE_VARIATIONS.length].id;
    const circleImage: RenderPackageImage = {
      type: RenderItemType.image,
      ...EMPTY_RENDER_TRANSFORM,
      positionX: position.x,
      positionY: position.y,
      positionLocalX: posB.x - circleWidth / 2,
      positionLocalY: posB.y - circleHeight / 2,
      scale: canvasLayerScale,
      imageId: circleImageId,
      shape: Shape.RECT,
      radius: 0,
      width: circleWidth,
      height: circleHeight,
      flipHorizontally: false,
    };
    rPackage.push(circleImage);

    if (anchor.label) {
      const label: RenderPackageText = {
        type: RenderItemType.text,
        ...EMPTY_RENDER_TRANSFORM,
        positionX: position.x,
        positionY: position.y,
        positionLocalX: posB.x + anchor.radius + LABEL_GAP,
        positionLocalY: posB.y - LABEL_FONT_SIZE / 2,
        scale: canvasLayerScale,
        hAlign: TextAlignHor.LEFT,
        text: anchor.label,
        size: LABEL_FONT_SIZE,
        fontId: 'font_regular',
        fillColor: anchor.color,
        strokeWeight: 0,
        strokeColor: COLORS.BLACK_100,
        shadow: null,
      };
      rPackage.push(label);
    }
  }

  return rPackage;
}
